<template>
  <div
    v-if="chartDataQuery.loading.value"
    class="_loading-container"
  >
    <m-spinner />
  </div>
  <div
    v-else-if="chartDataQuery.error.value !== null"
    class="_alert"
  >
    <m-alert
      type="error"
      :message="chartDataQuery.error.value"
    />
  </div>
  <MetricDisplay
    v-else-if="chart.chartType === gridPageChartType.metric"
    :value="metricResult"
    :unit="unit"
    :loading="chartDataQuery.loading.value"
    :size="size"
    :color="metricColor"
    :title="metricTitle"
    :title-enabled="metricTitleEnabled"
    :clickable="isMetricClickable"
    @activate="handleMetricClick"
  />
  <GridPageChartDataTable
    v-else-if="chart.chartType === gridPageChartType.table"
    :chart="chart"
    :chart-data-query="chartDataQuery"
  />
  <chart
    v-else
    :key="`${chart.chartType}-${aggregator}-${chart.stacked ?? true}`"
    :options="chartOptions"
    :class="['_chart', { '-clickable': isClickable }]"
  />
  <m-dialog
    v-model:value="showGoalList"
    :max-width="maxDialogWidth"
    hide-footer
    no-padding
    keep-height
    center
    :title="goalListTitle"
  >
    <temp-goal-list
      v-if="showGoalList"
      :id="tempGoalListId"
      :default-filter="goalListFilter"
      :title="goalListTitle"
      :goal-cycles="goalListCycles"
      :hide-cycle-selector="hideCycleSelector"
    />
  </m-dialog>
</template>

<script setup>
import GridPageChartDataTable from '@/components/custom-grid/GridPageChartDataTable.vue';
import MetricDisplay from '@/components/custom-grid/chart/MetricDisplay.vue';
import TempGoalList from '@/components/TempGoalList.vue';
import useBarChart from '@/composables/grid-page/chart/bar-chart';
import useChartDisplay from '@/composables/chart-display';
import useChartPointClick from '@/composables/grid-page/chart/chart-point-click';
import useGoalProgressChart from '@/composables/grid-page/chart/goal-progress-chart';
import useLineChart from '@/composables/grid-page/chart/line-chart';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePieChart from '@/composables/grid-page/chart/pie-chart';
import useStatusDistributionChart from '@/composables/grid-page/chart/status-distribution-chart';
import useStatusScoreChart from '@/composables/grid-page/chart/status-score-chart';
import { Chart } from 'highcharts-vue';
import { TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { aggregatorType, coloringMode, gridPageChartType } from 'shared/constants.json';
import { computed, toRef } from 'vue';
import { evaluateColoringRules } from '@/lib/coloring-rules';
import { goal as goalConfig } from 'shared/api/query/configs.json';

const props = defineProps({
  chart: { type: Object, required: true },
  size: { type: String, default: 'medium', validator: (value) => ['small', 'medium', 'large'].includes(value) },
  chartDataQuery: { type: Object, required: true },
  idPrefix: { type: String, required: true },
});

const { loggedInUserAccount } = useLoggedInUserAccount();

const {
  showGoalList,
  goalListFilter,
  goalListTitle,
  goalListCycles,
  hideCycleSelector,
  maxDialogWidth,
  tempGoalListId,
  handlePointClick: openGoalList,
  handleMetricClick,
  isClickable,
} = useChartPointClick({
  chart: toRef(props, 'chart'),
  chartDataQuery: props.chartDataQuery,
  accountUid: computed(() => loggedInUserAccount.value.uid),
  idPrefix: props.idPrefix,
});

const isMetricClickable = computed(() => props.chart.source === goalConfig.model
  && props.chart.chartType === gridPageChartType.metric);

const handlePointClick = computed(() => (isClickable.value
  ? (categoryIndex) => openGoalList({ categoryIndex })
  : undefined));

const aggregator = computed(() => props.chart.aggregationConfig?.aggregation?.aggregator);
const isTimeSeriesMode = computed(() => TIME_SERIES_AGGREGATORS.includes(aggregator.value));

const result = props.chartDataQuery.result;

const { chartOptions: pieChartOptions } = usePieChart(result, toRef(props, 'chart'), handlePointClick);
const { chartOptions: lineChartOptions } = useLineChart(result, toRef(props, 'chart'), handlePointClick);
const { chartOptions: barChartOptions } = useBarChart(result, toRef(props, 'chart'), handlePointClick);
const { chartOptions: progressChartOptions } = useGoalProgressChart(result, toRef(props, 'chart'));
const { chartOptions: statusScoreChartOptions } = useStatusScoreChart(result, toRef(props, 'chart'));
const { chartOptions: statusDistributionChartOptions } = useStatusDistributionChart(result, toRef(props, 'chart'), { showLegend: true });

const chartOptions = computed(() => {
  const baseOptions = { chart: { animation: false } };

  if (isTimeSeriesMode.value) {
    switch (aggregator.value) {
      case aggregatorType.statusScoreOverTime:
        return statusScoreChartOptions.value;
      case aggregatorType.statusDistributionOverTime:
        return statusDistributionChartOptions.value;
      case aggregatorType.progressOverTime:
        return progressChartOptions.value;
      default:
        return baseOptions;
    }
  }

  switch (props.chart.chartType) {
    case gridPageChartType.column:
    case gridPageChartType.bar:
      return barChartOptions.value;
    case gridPageChartType.line:
      return lineChartOptions.value;
    case gridPageChartType.pie:
      return pieChartOptions.value;
    default:
      return baseOptions;
  }
});

const metricResult = computed(() => {
  if (props.chart.chartType !== gridPageChartType.metric || props.chartDataQuery.result.value === null) {
    return null;
  }

  return Number(props.chartDataQuery.result.value.toFixed(2));
});

const { metricUnit } = useChartDisplay();

const unit = computed(() => metricUnit(props.chart.aggregationConfig.aggregation, props.chart.source));

const metricColor = computed(() => {
  if (props.chart.coloringMode === coloringMode.dynamic) {
    const rules = (props.chart.coloringRules || [])
      .filter((r) => !r.deletedAt)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    return evaluateColoringRules(metricResult.value, rules);
  }
  return props.chart.color || null;
});

const metricTitleEnabled = computed(() => props.chart.chartType === gridPageChartType.metric
  && !!props.chart.metricTitleEnabled);

const metricTitle = computed(() => {
  if (!metricTitleEnabled.value) return '';
  return props.chart.metricTitle ?? '';
});
</script>

<style scoped lang="scss">
._alert {
  width: 100%;
}

._chart {
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  min-height: 0;

  &.-clickable {
    :deep(.highcharts-plot-background) {
      cursor: pointer;
    }
  }
}

._loading-container {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
